import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useForm from '../use-form';
import FormError from '../FormReponse/FormError';
import FormMessage from '../FormReponse/FormMessage';
import FormSuccess from '../FormReponse/FormSuccess';
import FormRenderer from './FormRenderer'
import ModalFormContext from '../../../store/ModalFormContext';
import PostFormButton from '../Buttons/PostFormButton';
import Icon from '../../../icons/Icon';
import { LearningTooltip } from '../../LeaningTooltip/LearningTooltip';
import useJSONActions from '../../../hooks/use-json-actions';
import { buildFormSubmitPayload } from '../functions/buildFormSubmitPayload';
import { buildCompleteFormPayload } from '../functions/completeFormPayload';

const FormHandler = ({ index, form_id, formIndex, form, forms, classes, inline, create_new,  changeForm, saveHook, dispatchForms}) => {
    const ref = useRef();
    const hasFocusedRef = useRef(false);
    const context = useContext(ModalFormContext)
    const [showSuccess, setShowSuccess] = useState(false);
    const [removeErrors, setRemoveErrors] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [deleteUrl, setDeleteUrl] = useState(false);
    const [formValues, setFormValues] = useState(form.values || {});
    const [uploadingFields, setUploadingFields] = useState({});
    const getJsonActions = useJSONActions()

    useEffect(() => {
      if(form.allow_delete && form.pk){
        const actions = getJsonActions(null, {'action_id':form.allow_delete })
        setDeleteUrl(actions[0]?.form_url?.replace('<pk>', form.pk))
      }
      else{
        setDeleteUrl(false)
      }
    }, [form.allow_delete, form.pk])

    const prevFormValuesRef = useRef(null);
    const lastFormIdRef = useRef(undefined);

    useEffect(() => {
      if (lastFormIdRef.current !== form_id) {
        lastFormIdRef.current = form_id;
        prevFormValuesRef.current = form?.values ? JSON.stringify(form.values) : null;
        setFormValues(form.values || {});
        return;
      }

      if (!form?.values) return;
      const valuesKey = JSON.stringify(form.values);
      if (prevFormValuesRef.current === valuesKey) return;
      prevFormValuesRef.current = valuesKey;

      setFormValues((cur) => {
        const next = { ...(cur || {}) };
        Object.keys(form.values).forEach((key) => {
          const serverValue = form.values[key];

          const isMeaningful =
            serverValue !== undefined &&
            serverValue !== null &&
            serverValue !== '' &&
            !(Array.isArray(serverValue) && serverValue.length === 0);

          if (isMeaningful || !(key in next)) {
            next[key] = serverValue;
          }
        });
        return next;
      });
    }, [form?.values, form_id]);

    // Map functions to form
    useEffect(() => {
      if(forms.length === 1 && !hasFocusedRef.current && form?.fields){
        // Use requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          const formElement = ref.current;
          if(formElement && formElement.elements.length > 0){
            formElement.elements[0].focus()
            hasFocusedRef.current = true;
          }
        });
      }
    }, [form?.fields, forms.length]);
  

    useEffect(() => {
      if (form['success']) {
          setShowSuccess(true);
          const timer = setTimeout(() => {
              setShowSuccess(false);
              form['success'] = false;
          }, 1000);
          return () => clearTimeout(timer);
      }
    }, [form['success']]);
  
    const formHandler = useCallback(async (form) => {
      const newForms = [...forms]
      if (form.valid){
        if(form.redirect_url && form.redirect_url.startsWith('/rest/')){
          context.setModalForm({url:form.redirect_url, form_id:form.form_id, callbackFunc:context.callbackFunc});
        } 
        newForms[formIndex] = {...newForms[formIndex], ...form, index: formIndex}
      }
      else if(form.status === 422){
        newForms[formIndex]= {...form, index: formIndex}
      }
      
      dispatchForms({type:'REPLACE_FORM', forms: [{...newForms[formIndex], ...form}], index: formIndex})
      if(form.valid && saveHook){
        saveHook(newForms, formIndex)
      }
    }, [forms, formIndex, saveHook, context])

    const {isLoading : formLoading, error : formError, sendRequest : submitFormData} = useForm(formHandler)
    const submitForm = useCallback(async (data = null, addAnother = false, autoSaved = false, reload = false) => {
      if (Object.values(uploadingFields).some(Boolean)) {
        return;
      }
      let payload = await buildFormSubmitPayload(buildCompleteFormPayload(form, data ?? formValues))
      if (addAnother) {
        payload = { ...payload, __addAnother__: true }
      }
      const reloadForm = (reload && !autoSaved)
      const url = form.action.toString()
      submitFormData({ url, method: form.method, data: payload, autoSaved, reload: reloadForm })
    }, [submitFormData, form, formValues])

    const debounceTimeout = useRef(null);

    const handleChange = useCallback((e, auto_save = false, reload = false) => {
      if (Object.prototype.hasOwnProperty.call(e, 'uploading')) {
        setUploadingFields((current) => ({
          ...current,
          [e.name]: Boolean(e.uploading)
        }));
      }
      if (formValues[e.name] == e.value) return null;
      setFormValues({...formValues, [e.name]: e.value})
      if (!form.auto_save && !auto_save && !reload) return;
      var timeout = 1000;
      if(auto_save || reload){
        timeout = 0;
      }
      // Clear any existing timeout
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      // Set a new timeout
      debounceTimeout.current = setTimeout(() => {
        submitForm({...formValues, [e.name]: e.value}, false,  (form.auto_save || auto_save), reload);
      }, timeout);
    }, [form.auto_save, form, formValues, submitForm]);

    const hasUploadingFields = Object.values(uploadingFields).some(Boolean)
    const disabled = (formLoading || hasUploadingFields || (formError && !removeErrors))

    const handleKeyDown = useCallback((e) => {
      if (!disabled && e.key === 'Enter' && e.ctrlKey && form.add_another_button) {
        e.preventDefault();
        submitForm(null, true);
      }
    }, [disabled, form.add_another_button, submitForm]);

    
    if(deleted) return
    return (
        <>
          <form 
            className={`position-relative ${classes} ${(form.inline) ? 'rest-form-inline' : 'rest-form'}`} 
            ref={ref} 
            id={"api-form" + formIndex} 
            method={form.method} 
            action={form.action} 
            style={{maxWidth: '100%', ...form.form_style}}
            onKeyDown={handleKeyDown}
            >
            <FormError formError={formError} errors={form.errors} setRemoveErrors={setRemoveErrors}/>
            <FormMessage message={form.message}/>
            {showSuccess && <FormSuccess success={form['success']} />}
              <div className={`${(form.inline) ? 'display-inline' : 'form-wrapper'}`}>
                <FormRenderer onChange={handleChange} index={index} formIndex={formIndex} inline={form.inline} id={"api-form" + formIndex} fields={form.fields} values={formValues} />
                { deleteUrl 
                  && <PostFormButton url={deleteUrl} callbackFunc={() => setDeleted(true)} className="btn btn-outline-danger mt-auto btn-sm" confirm_delete={true} >
                    <Icon name="ICON_TRASH" className="" />
                  </PostFormButton>
                }
              </div>
              {(form.inline) 
                ? 
                  (!form.auto_save)? 
                  <input tabIndex={0} onClick={() => submitForm(null)} className={form.button?.class ? `btn ${form.button.class}` : "btn btn-outline-dark btn-sm save-inline my-auto"} disabled={disabled} type="submit" value={form.button?.text ?? form.button} />  
                  :null
                :
                <div className='save-button bg-white d-flex flex-column' style={{'zIndex':'3'}}>
                  <div className="btn-group mx-auto ">
                    {form.button && !form.auto_save ? 
                        form.button.class ?
                          <input tabIndex={0} onClick={() => submitForm(null)} className={`btn ${form.button.class}`} disabled={disabled} type="submit" value={form.button.text}  data-form-type="action"/>  
                          :
                          <input tabIndex={0} onClick={() => submitForm(null)} className="btn btn-outline-primary" disabled={disabled} type="submit" value={form.button}  data-form-type="action"/>  
                        :null
                    }

                    { (create_new) ? 
                      null
                      : ''
                    }
                    { 
                      (form.add_another_button) ? 
                      <LearningTooltip
                          id="add-another-button-tooltip"
                          content="Add another with ctrl+enter"
                          placement="bottom"
                          // delay={2000}
                        >
                        <input title="Ctrl + Enter" onClick={e => submitForm(null, true)} className="btn btn-outline-secondary"  type="submit" value={form.add_another_button} disabled={disabled} name="addAnother" data-form-type="action" />
                      </LearningTooltip>
                      :null
                    }
                  </div>
              </div>
            }
          </form>
        </>
    )
}

export default FormHandler;
